<template>
  <Teleport to="body">
    <transition name="palette-fade">
      <div v-if="isOpen" class="palette-backdrop" @click="close">
        <transition name="palette-slide">
          <div v-if="isOpen" class="palette" @click.stop>
            <!-- Search Input -->
            <div class="palette-input-wrap">
              <MdpsIcon name="search" size="small" class="palette-search-icon"/>
              <input
                  ref="inputRef"
                  v-model="query"
                  class="palette-input"
                  placeholder="Type a command or search..."
                  @keydown.escape="close"
                  @keydown.down.prevent="selectNext"
                  @keydown.up.prevent="selectPrev"
                  @keydown.enter.prevent="executeSelected"
              />
              <kbd class="palette-kbd">ESC</kbd>
            </div>

            <!-- Results -->
            <div class="palette-results" v-if="filteredItems.length">
              <div
                  v-for="(group, groupName) in groupedResults"
                  :key="groupName"
                  class="palette-group"
              >
                <div class="palette-group-label">{{ groupName }}</div>
                <div
                    v-for="item in group"
                    :key="item.id"
                    :class="['palette-item', { active: item.id === activeId, disabled: item.disabled }]"
                    @click="execute(item)"
                    @mouseenter="activeId = item.id"
                >
                  <div class="palette-item-icon">
                    <MdpsIcon :name="item.icon" size="small"/>
                  </div>
                  <div class="palette-item-content">
                    <span class="palette-item-title">{{ item.title }}</span>
                    <span v-if="item.description" class="palette-item-desc">{{ item.description }}</span>
                  </div>
                  <kbd v-if="item.shortcut" class="palette-item-shortcut">{{ item.shortcut }}</kbd>
                </div>
              </div>
            </div>

            <!-- Empty State (query but no results) -->
            <div v-else-if="query.length > 0" class="palette-empty">
              <MdpsIcon name="search" size="large" class="palette-empty-icon"/>
              <p class="palette-empty-title">No results for "{{ query }}"</p>
              <p class="palette-empty-hint">Try searching for pages, initiatives, or actions</p>
            </div>

            <!-- Default: show all items when no query -->
            <div v-else class="palette-results">
              <div
                  v-for="(group, groupName) in allGrouped"
                  :key="groupName"
                  class="palette-group"
              >
                <div class="palette-group-label">{{ groupName }}</div>
                <div
                    v-for="item in group"
                    :key="item.id"
                    :class="['palette-item', { active: item.id === activeId, disabled: item.disabled }]"
                    @click="execute(item)"
                    @mouseenter="activeId = item.id"
                >
                  <div class="palette-item-icon">
                    <MdpsIcon :name="item.icon" size="small"/>
                  </div>
                  <div class="palette-item-content">
                    <span class="palette-item-title">{{ item.title }}</span>
                    <span v-if="item.description" class="palette-item-desc">{{ item.description }}</span>
                  </div>
                  <kbd v-if="item.shortcut" class="palette-item-shortcut">{{ item.shortcut }}</kbd>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="palette-footer">
              <span class="palette-footer-item"><kbd>↑↓</kbd> Navigate</span>
              <span class="palette-footer-item"><kbd>↵</kbd> Select</span>
              <span class="palette-footer-item"><kbd>esc</kbd> Close</span>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from 'vue'
import {useRouter} from 'vue-router'
import {useAuthStore} from '@/stores/authStore'
import MdpsIcon from '@mdps/icon-vue3'
import {showContributorUpgradeMessage} from '@/utils/roleMessages'

interface PaletteItem {
  id: string
  title: string
  description?: string
  icon: string
  group: string
  action: () => void
  disabled?: boolean
  shortcut?: string
  keywords?: string[]
}

const router = useRouter()
const authStore = useAuthStore()
const isOpen = ref(false)
const query = ref('')
const activeId = ref('')
const inputRef = ref<HTMLInputElement | null>(null)

const allItems = computed<PaletteItem[]>(() => {
  const items: PaletteItem[] = [
    {
      id: 'nav-home',
      title: 'Initiatives Hub',
      description: 'Platform home',
      icon: 'home',
      group: 'Pages',
      action: () => router.push('/'),
      keywords: ['landing', 'main']
    },
    {
      id: 'nav-search',
      title: 'Search',
      description: 'Find initiatives',
      icon: 'search',
      group: 'Pages',
      action: () => router.push('/search'),
      keywords: ['find', 'lookup', 'browse', 'list', 'initiatives']
    },
    {
      id: 'nav-dashboard',
      title: 'Dashboard',
      description: 'Initiative progress and collaboration metrics',
      icon: 'dashboard',
      group: 'Pages',
      action: () => router.push('/dashboard'),
      keywords: ['dashboard', 'metrics', 'stats', 'initiatives hub']
    },
    {
      id: 'nav-awards',
      title: 'Leaderboard',
      description: 'Points and recognition',
      icon: 'bullhorn',
      group: 'Pages',
      action: () => router.push('/leaderboard'),
      keywords: ['leaderboard', 'winners', 'scores']
    },
    {
      id: 'nav-profile',
      title: 'Profile',
      description: 'View your submissions & scores',
      icon: 'user',
      group: 'Pages',
      action: () => router.push('/profile'),
      keywords: ['account', 'me']
    },
    {
      id: 'nav-notifications',
      title: 'Notifications',
      description: 'View activity feed',
      icon: 'bell',
      group: 'Pages',
      action: () => router.push('/notifications'),
      keywords: ['alerts', 'activity']
    },
    {
      id: 'nav-contact',
      title: 'Contact support',
      description: 'Ask the Initiatives Hub team a question',
      icon: 'chat-bubble',
      group: 'Pages',
      action: () => router.push('/contact'),
      keywords: ['help', 'support', 'contact', 'question', 'feedback', 'stuck', 'access', 'email']
    },
    {
      id: 'nav-approvals',
      title: 'Approval Inbox',
      description: 'Review initiatives waiting for your decision',
      icon: 'check',
      group: 'Actions',
      action: () => router.push('/approvals'),
      keywords: ['manager', 'approval', 'approve', 'pending', 'queue']
    },
  ]

  items.push({
    id: 'action-submit',
    title: 'Register a new initiative',
    description: authStore.canCreateInitiative ? 'Create an initiative' : 'Contributor role required',
    icon: 'plus',
    group: 'Actions',
    disabled: !authStore.canCreateInitiative,
    action: () => {
      if (authStore.canCreateInitiative) router.push('/submit')
      else showContributorUpgradeMessage()
    },
    shortcut: '⌘N',
    keywords: ['create', 'new', 'add']
  })

  // Reports is not an admin surface, but the admin nav used to be its only
  // entry point — so it is gated on its own permission and grouped with the
  // rest of the app's pages.
  if (authStore.hasPermission('reports:view:all')) {
    items.push({
      id: 'nav-reports',
      title: 'Reports',
      description: 'Sub-BU, category, and lifecycle reporting',
      icon: 'chart-bar',
      group: 'Pages',
      action: () => router.push('/reports'),
      keywords: ['reports', 'export', 'reporting']
    })
  }

  if (authStore.canViewUsers) {
    items.push(
        {
          id: 'nav-admin-users',
          title: 'Users',
          description: 'Roles, org placement, and bulk import',
          icon: 'user-group',
          group: 'Admin',
          action: () => router.push('/admin/users'),
          keywords: ['admin', 'manage', 'roles', 'assign', 'bulk import', 'users']
        },
        {
          id: 'nav-admin-organization',
          title: 'Organization',
          description: 'Business Units, Sub-BUs, and approver routing',
          icon: 'settings',
          group: 'Admin',
          action: () => router.push('/admin/organization'),
          keywords: ['business unit', 'sub-bu', 'taxonomy', 'approver', 'approval routing']
        },
        {
          id: 'nav-admin-support',
          title: 'Support inbox',
          description: 'Contact messages and bug reports',
          icon: 'chat-bubble',
          group: 'Admin',
          action: () => router.push('/admin/support'),
          keywords: ['support', 'inbox', 'contact', 'bug', 'reports', 'triage', 'tickets', 'queries']
        },
    )
  }

  if (authStore.isAuthenticated) {
    items.push(
      {
        id: 'nav-evaluator-ratings',
        title: 'Evaluator Ratings',
          description: 'Score assigned initiatives',
          icon: 'graduation-cap',
          group: 'Pages',
          action: () => router.push('/ci/panelist-ratings'),
          keywords: ['score', 'rate', 'evaluate']
        },
    )
  }

  return items
})

const filteredItems = computed(() => {
  if (!query.value.trim()) return allItems.value
  const q = query.value.toLowerCase().trim()
  return allItems.value.filter(item => {
    const searchText = [item.title, item.description, ...(item.keywords || [])].join(' ').toLowerCase()
    return searchText.includes(q)
  })
})

const groupedResults = computed(() => {
  const groups: Record<string, PaletteItem[]> = {}
  for (const item of filteredItems.value) {
    if (!groups[item.group]) groups[item.group] = []
    groups[item.group].push(item)
  }
  return groups
})

const allGrouped = computed(() => {
  const groups: Record<string, PaletteItem[]> = {}
  for (const item of allItems.value) {
    if (!groups[item.group]) groups[item.group] = []
    groups[item.group].push(item)
  }
  return groups
})

watch(filteredItems, (items) => {
  if (items.length && !items.find(i => i.id === activeId.value)) {
    activeId.value = items[0].id
  }
}, {immediate: true})

function open() {
  isOpen.value = true
  query.value = ''
  activeId.value = ''
  nextTick(() => inputRef.value?.focus())
}

function close() {
  isOpen.value = false
  query.value = ''
}

function selectNext() {
  const items = filteredItems.value
  const idx = items.findIndex(i => i.id === activeId.value)
  if (idx < items.length - 1) activeId.value = items[idx + 1].id
  else if (items.length > 0) activeId.value = items[0].id
}

function selectPrev() {
  const items = filteredItems.value
  const idx = items.findIndex(i => i.id === activeId.value)
  if (idx > 0) activeId.value = items[idx - 1].id
  else if (items.length > 0) activeId.value = items[items.length - 1].id
}

function executeSelected() {
  const item = filteredItems.value.find(i => i.id === activeId.value)
  if (item) execute(item)
}

function execute(item: PaletteItem) {
  close()
  item.action()
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    isOpen.value ? close() : open()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

defineExpose({open, close, isOpen})
</script>

<style scoped>
.palette-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 99999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
}

.palette {
  width: 580px;
  max-width: calc(100vw - 2rem);
  max-height: 460px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18),
  0 0 0 1px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.palette-fade-enter-active {
  transition: opacity 0.2s ease;
}

.palette-fade-leave-active {
  transition: opacity 0.12s ease;
}

.palette-fade-enter-from, .palette-fade-leave-to {
  opacity: 0;
}

.palette-slide-enter-active {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s;
}

.palette-slide-leave-active {
  transition: transform 0.15s ease, opacity 0.1s;
}

.palette-slide-enter-from {
  transform: scale(0.95) translateY(-12px);
  opacity: 0;
}

.palette-slide-leave-to {
  transform: scale(0.98) translateY(-4px);
  opacity: 0;
}

.palette-input-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.palette-search-icon {
  flex-shrink: 0;
  color: #94a3b8;
}

.palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
  font-family: inherit;
  color: #1e293b;
  background: transparent;
}

.palette-input::placeholder {
  color: #94a3b8;
}

.palette-kbd {
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 0.62rem;
  font-family: -apple-system, monospace;
  color: #64748b;
}

.palette-results {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  scrollbar-width: thin;
}

.palette-group {
  margin-bottom: 0.25rem;
}

.palette-group-label {
  padding: 0.5rem 0.75rem 0.35rem;
  font-size: 0.67rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
}

.palette-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.1s;
}

.palette-item.active {
  background: #f1f5f9;
}

.palette-item.disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.palette-item.disabled.active {
  background: #f8fafc;
}

.palette-item-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #64748b;
  transition: all 0.1s;
}

.palette-item.active .palette-item-icon {
  background: #eff6ff;
  border-color: #dbeafe;
  color: #3b82f6;
}

.palette-item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.palette-item-title {
  font-size: 0.85rem;
  font-weight: 550;
  color: #1e293b;
}

.palette-item-desc {
  font-size: 0.72rem;
  color: #94a3b8;
}

.palette-item-shortcut {
  padding: 0.15rem 0.5rem;
  border-radius: 5px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.6rem;
  font-family: -apple-system, monospace;
  color: #64748b;
}

.palette-empty {
  padding: 2.5rem 1.25rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.palette-empty-icon {
  color: #cbd5e1;
}

.palette-empty-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #475569;
}

.palette-empty-hint {
  margin: 0;
  font-size: 0.78rem;
  color: #94a3b8;
}

.palette-footer {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.6rem 1.25rem;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.palette-footer-item {
  font-size: 0.68rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.palette-footer kbd {
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  font-family: -apple-system, monospace;
  font-size: 0.6rem;
  color: #475569;
}

@media (max-width: 640px) {
  .palette-backdrop {
    padding-top: 3vh;
  }

  .palette {
    max-height: 80vh;
    border-radius: 12px;
    margin: 0.5rem;
  }
}
</style>
